import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TranslateService } from '@shared/translate/translate.service';
import {
  FundCommentaryContent,
  FundCommentaryDocsResponse,
  FundCommentaryDocument,
} from '@types';
import {
  API_DATE_FORMAT,
  COMMENTARY,
  LONG_DATE_FORMAT,
  MEDIUM_DATE_FORMAT,
  MONTHLY_COMMENTARY,
  QUARTERLY_COMMENTARY,
} from '@utils/app.constants';
import { createDateComparator } from '@utils/sort-utils';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { FundCommentaryService } from './services/fund-commentary.service';
@Component({
  selector: 'ft-fund-commentary',
  templateUrl: './fund-commentary.component.html',
  providers: [DatePipe],
})
export class FundCommentaryComponent
  extends AbstractBaseComponent
  implements OnInit {
  // ToDo : fix type
  fundCommentaryContent$: Observable<FundCommentaryContent>;
  fundCommentaryDocuments$: Observable<FundCommentaryDocsResponse>;
  monthlyFundCommentaryDocuments$: Observable<FundCommentaryDocument[]>;
  quarterlyFundCommentaryDocuments$: Observable<FundCommentaryDocument[]>;
  downloadDocument$: Observable<FundCommentaryDocument>;
  fundCommentaryDocContinueRead$: Observable<boolean>;
  fundCommentaryContentContinueRead$: Observable<boolean>;
  fullDescription: boolean;
  profile$: Observable<IUserProfile>;
  commentaryTitle: string;
  monthlyCommentaryTitle: string;
  showDefaultTitle$: Observable<boolean>;
  showQuaterlyDocsTitle$: Observable<boolean>;
  publicationDate: string;

  private fundCommentaryContentSubject$ = new Subject<FundCommentaryDocsResponse>();
  private monthlyCom = MONTHLY_COMMENTARY;
  private quarterCom = QUARTERLY_COMMENTARY;

  constructor(
    private fundCommentaryService: FundCommentaryService,
    private dateFormatter: DatePipe,
    private profileService: ProfileService,
    private translateService: TranslateService,
    private literatureConfig: LiteratureGlobalConfigService
  ) {
    super();
  }
  /**
   * Extends view area to display all commentary data
   */
  continueReading(): void {
    this.fullDescription = !this.fullDescription;
  }
  ngOnInit() {
    this.profile$ = this.fundCommentaryService.getProfileData$();
    this.commentaryTitle = this.translateService.instant(
      'literature.commentary-title'
    );
    this.monthlyCommentaryTitle = this.translateService.instant(
      'literature.monthly-commentary-title'
    );

    const config = this.component.getModels()?.config;

    // waiting for commentary documents data and content
    this.fundCommentaryContent$ = this.getFundCommentaryContent().pipe(
      map((documentData) => {
        const activeCommentaryDoc = this.getActiveCommentaryDoc(documentData);
        if (activeCommentaryDoc) {
          this.publicationDate = this.dateFormatter.transform(
            activeCommentaryDoc?.publicationDate,
            MEDIUM_DATE_FORMAT
          );
        }
        return documentData?.commentary;
      })
    );

    // contains list of commentary documents
    this.fundCommentaryDocuments$ = this.fundCommentaryService
      .getCommentaryDocs$({
        fundId: config?.fundId,
        shareClassCode: config?.shareClassCode,
      })
      .pipe(
        shareReplay(1),
        tap((data) => {
          if (data.document) {
            this.fundCommentaryContentSubject$.next(data);
          }
          return data;
        })
      );

    this.downloadDocument$ = this.fundCommentaryDocuments$.pipe(
      map((data: FundCommentaryDocsResponse) => cloneDeep(data)),
      map((data) => this.getActiveCommentaryDoc(data)),
      map((item) => {
        if (item) {
          item.downloadPdfUrl = this.mapPdfURL(item);
        }
        if (item.publicationDate) {
          item.publicationDate = this.dateFormatter.transform(
            item?.publicationDate,
            MEDIUM_DATE_FORMAT
          );
        }
        return item;
      })
    );
    // list of month commentary documents are filtered
    this.monthlyFundCommentaryDocuments$ = this.fundCommentaryDocuments$.pipe(
      map((data) => cloneDeep(data)),
      map((data) =>
        data?.document?.filter(
          (item) => item.dctermsType === MONTHLY_COMMENTARY
        )
      ),
      map((item) => this.sortData(item).slice(0, 15)),
      map((data) => this.modifyCommentryProperties(data))
    );
    // list of quarterly commentary documents are filtered
    this.quarterlyFundCommentaryDocuments$ = this.fundCommentaryDocuments$.pipe(
      map((data) => cloneDeep(data)),
      map((data) => this.checkQuarterlyFundCommentaryDocuments(data)),
      map((item) => this.sortData(item).slice(0, 7)),
      map((data) => this.modifyCommentryProperties(data))
    );
    this.fundCommentaryDocContinueRead$ = this.fundCommentaryDocuments$.pipe(
      map((data) => data?.document?.length > 3)
    );
    this.fundCommentaryContentContinueRead$ = this.fundCommentaryContent$.pipe(
      map((data) => (data ? true : false))
    );
    this.showDefaultTitle$ = this.fundCommentaryContent$.pipe(
      map((data) => (data ? true : false))
    );
    this.showQuaterlyDocsTitle$ = this.quarterlyFundCommentaryDocuments$.pipe(
      map((data) => data?.length > 0)
    );
  }

  /**
   * check and return quaterly fund commentary documents based on the condition
   * @param data FundCommentaryDocsResponse
   * @returns FundCommentaryDocument[]
   */
  checkQuarterlyFundCommentaryDocuments(
    data: FundCommentaryDocsResponse
  ): FundCommentaryDocument[] {
    return data?.document?.filter((item) =>
      data?.document.length === 1
        ? item.dctermsType === QUARTERLY_COMMENTARY
        : item.dctermsType === QUARTERLY_COMMENTARY && item.isHistoryData
    );
  }

  /**
   * gets the active commentary document based on the condition
   * @param data FundCommentaryDocsResponse
   * @returns FundCommentaryDocument
   */
  getActiveCommentaryDoc(
    data: FundCommentaryDocsResponse
  ): FundCommentaryDocument {
    return data.document.find(
      (doc) =>
        doc.isHistoryData === false &&
        (doc.dctermsType === COMMENTARY ||
          doc.dctermsType === QUARTERLY_COMMENTARY ||
          (data.document.length === 1 &&
            doc.dctermsType === MONTHLY_COMMENTARY))
    );
  }

  getFundCommentaryContent(): Observable<FundCommentaryDocsResponse> {
    return this.fundCommentaryContentSubject$.asObservable();
  }

  /**
   * @param data document object
   * @returns document object sorted in descending order of publicationDate
   */

  sortData(data: FundCommentaryDocument[]): FundCommentaryDocument[] {
    const dateCompare = createDateComparator(API_DATE_FORMAT);
    return data
      .sort((current, next) => {
        return dateCompare(
          current.publicationDate,
          next.publicationDate,
          null,
          null,
          false
        );
      })
      .reverse();
  }
  mapPdfURL(doc: FundCommentaryDocument): string {
    return this.fundCommentaryService.getDocumentDownloadUrl(doc);
  }

  /**
   *
   * @param data array of commentary document
   * @returns publicationDate formatted in the given format
   */
  modifyCommentryProperties(
    data: FundCommentaryDocument[]
  ): FundCommentaryDocument[] {
    data?.forEach((item) => {
      if (item) {
        item.downloadPdfUrl = this.mapPdfURL(item);
        if (item.publicationDate) {
          item.publicationDate = this.dateFormatter.transform(
            item?.publicationDate,
            MEDIUM_DATE_FORMAT
          );
        }
      }
    });
    return data;
  }
}
